import type {
  DashboardNavItem,
  DashboardSectionKey,
  DashboardUserProfile,
} from "./dashboard.types.ts";

// The IFR application is a separate deployment for now and opens in a new tab.
// TODO: drop this entry entirely once IFR is folded into this application.
const IFR_NON_PROD_URL =
  "https://rtg-businessapps-ifr3-dev.azurewebsites.net/orderproducts";
const IFR_PROD_URL =
  "https://businessapps-rtg-businessapps-ifr3.roomstogo.com/products";

// Every non-production host points at the non-prod IFR deployment: the dev, qa,
// and stage app hosts plus local development. Only production falls through to
// the production URL, so a new environment defaults to the safe target rather
// than sending testers into live IFR data.
const NON_PROD_HOSTNAME_TOKENS = [
  "dev",
  "qa",
  "stage",
  "localhost",
  "127.0.0.1",
];

// Matches on hostname, not the full URL, so a query string such as ?env=dev
// cannot flip the link.
export function resolveIfrAppUrl(hostname: string): string {
  const normalizedHostname = hostname.toLowerCase();
  return NON_PROD_HOSTNAME_TOKENS.some((token) =>
    normalizedHostname.includes(token)
  )
    ? IFR_NON_PROD_URL
    : IFR_PROD_URL;
}

export const dashboardNavigationItems: DashboardNavItem[] = [
  {
    kind: "route",
    key: "insight-iq",
    label: "Insight.IQ",
    routeName: "dashboard-overview",
    icon: "◎",
    description: "Redistribution impact across supply chain metrics.",
    availableInSprint: true,
  },
  {
    kind: "group",
    key: "redisku",
    label: "RediSKU",
    icon: "⇄",
    description: "Purchase order redistribution workflow and analysis.",
    availableInSprint: true,
    children: [
      {
        kind: "route",
        key: "intermediate-order-sku-distribution",
        label: "Intermediate Order SKU Distribution",
        routeName: "dashboard-intermediate-order-sku-distribution",
        icon: "▤",
        description: "Manage SKU distribution across Intermediate Orders.",
        availableInSprint: true,
      },
      {
        kind: "route",
        key: "bulk-order-creation",
        label: "Shipping PO Creation",
        routeName: "dashboard-bulk-order-creation",
        icon: "▥",
        description: "Find an order and review its SKU lines before creation.",
        availableInSprint: true,
      },
      {
        kind: "route",
        key: "po-recommendations",
        label: "PO Recommendations",
        routeName: "search-select-pos",
        icon: "⇄",
        description: "Start a purchase-order redistribution run.",
        availableInSprint: true,
        disabled: true,
        childrenGatedByResults: true,
        children: [
          {
            kind: "route",
            key: "results",
            label: "Results",
            routeName: "results",
            icon: "✅",
            description: "View the latest redistribution results.",
            availableInSprint: true,
          },
          {
            kind: "route",
            key: "sku-analysis",
            label: "SKU Analysis",
            routeName: "dashboard-sku-analysis",
            icon: "▣",
            description: "Inspect SKU-level risk and opportunity trends.",
            availableInSprint: true,
          },
          {
            kind: "route",
            key: "inventory-health",
            label: "Inventory Health",
            routeName: "dashboard-inventory-health",
            icon: "◔",
            description:
              "Track before-and-after inventory health improvements.",
            availableInSprint: true,
          },
          {
            kind: "route",
            key: "transfers",
            label: "Transfers",
            routeName: "dashboard-transfers",
            icon: "↔",
            description: "Review movement decisions and transfer readiness.",
            availableInSprint: false,
          },
        ],
      },
    ],
  },
  {
    kind: "external",
    key: "ifr",
    label: "IFR",
    href: resolveIfrAppUrl(globalThis.location.hostname),
    icon: "↗",
    description: "Open the IFR application in a new tab.",
    availableInSprint: true,
  },
  {
    kind: "route",
    key: "reports",
    label: "Reports",
    routeName: "dashboard-reports",
    icon: "☰",
    description: "Access scheduled exports and operational reports.",
    availableInSprint: true,
  },
  {
    kind: "route",
    key: "settings",
    label: "Settings",
    routeName: "dashboard-settings",
    icon: "⚙",
    description: "Manage dashboard defaults and control-tower preferences.",
    availableInSprint: false,
    pinToBottom: true,
  },
];

export const dashboardSectionLabels: Record<DashboardSectionKey, string> = {
  distributionCenterActivity: "In process by distribution center",
  summaryStats: "Summary stat cards",
  insightPanels: "Insight panels",
  tables: "Snapshot tables",
  footerSummary: "Footer run summary",
};

export const dashboardUserProfile: DashboardUserProfile = {
  initials: "RTG",
  name: "Rooms To Go",
  role: "Supply Chain Planning",
};
